if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          mobileDots: this.querySelector('[data-gallery-dots]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.isAppleTouchDevice =
          /iPad|iPhone|iPod/.test(navigator.userAgent) ||
          (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        this.mobileSlider = this.elements.viewer.slider || this.elements.viewer.querySelector('[id^="Slider-"]');
        this.mobileMediaItems = this.mobileSlider
          ? Array.from(this.mobileSlider.querySelectorAll(':scope > [data-media-id]'))
          : [];
        this.mobileIndex = Math.max(
          0,
          this.mobileMediaItems.findIndex((mediaItem) => mediaItem.classList.contains('is-active'))
        );
        this.mobileHasInteracted = false;
        this.mobileCloneHydrationTimer = null;
        this.mobileCloneIdleHandle = null;

        this.onSlideChangedBound = this.onSlideChanged.bind(this);
        this.elements.viewer.addEventListener('slideChanged', this.onSlideChangedBound);

        if (this.mobileSlider) {
          this.onMobilePointerDownBound = this.onMobilePointerDown.bind(this);
          this.onMobilePointerMoveBound = this.onMobilePointerMove.bind(this);
          this.onMobilePointerUpBound = this.onMobilePointerUp.bind(this);
          this.onMobilePointerCancelBound = this.onMobilePointerCancel.bind(this);
          this.onMobileTouchStartBound = this.onMobileTouchStart.bind(this);
          this.onMobileTouchMoveBound = this.onMobileTouchMove.bind(this);
          this.onMobileTouchEndBound = this.onMobileTouchEnd.bind(this);
          this.onMobileTouchCancelBound = this.onMobileTouchCancel.bind(this);
          this.onMobileWindowScrollBound = this.onMobileWindowScroll.bind(this);
          this.onMobileClickBound = this.onMobileClick.bind(this);
          this.onMobileNavigationClickBound = this.onMobileNavigationClick.bind(this);
          this.onMobileResizeBound = this.onMobileResize.bind(this);
          this.onPageShowBound = () => this.restoreMobileGallery(true);
          this.onVisibilityChangeBound = () => {
            if (document.visibilityState === 'visible') this.syncMobileVisibleMedia(true);
          };
          this.onMediaQueryChangeBound = () => {
            this.cancelMobileAnimation();
            this.mobilePointerState = null;
            this.syncVideoPresentation();
            if (this.mql.matches) this.resetDesktopGallery();
            else requestAnimationFrame(() => this.restoreMobileGallery(true));
          };

          this.mobileSlider.classList.add('is-transform-gallery');
          this.mobileSlider.addEventListener('pointerdown', this.onMobilePointerDownBound);
          this.mobileSlider.addEventListener('touchstart', this.onMobileTouchStartBound, { passive: true });
          this.mobileSlider.addEventListener('click', this.onMobileClickBound, true);
          this.elements.viewer.addEventListener('click', this.onMobileNavigationClickBound, true);
          window.addEventListener('resize', this.onMobileResizeBound, { passive: true });
          window.addEventListener('pageshow', this.onPageShowBound);
          document.addEventListener('visibilitychange', this.onVisibilityChangeBound);
          if (this.mql.addEventListener) this.mql.addEventListener('change', this.onMediaQueryChangeBound);
          else this.mql.addListener(this.onMediaQueryChangeBound);

          this.syncVideoPresentation();
          if (!this.mql.matches) requestAnimationFrame(() => this.restoreMobileGallery(true));
        }

        this.initMobileDots();

        if (!this.elements.thumbnails) return;
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      disconnectedCallback() {
        this.elements.viewer.removeEventListener('slideChanged', this.onSlideChangedBound);
        if (this.mobileSlider) {
          this.mobileSlider.removeEventListener('pointerdown', this.onMobilePointerDownBound);
          this.mobileSlider.removeEventListener('touchstart', this.onMobileTouchStartBound);
          this.unbindMobilePointerEvents();
          this.unbindMobileTouchEvents();
          this.mobileSlider.removeEventListener('click', this.onMobileClickBound, true);
          this.elements.viewer.removeEventListener('click', this.onMobileNavigationClickBound, true);
          this.mobileSlider.classList.remove('is-transform-gallery', 'is-dragging', 'is-settling');
          this.mobileSlider.style.removeProperty('transform');
          this.mobileSlider.style.removeProperty('transition');
          this.removeMobileLoopClones();
          this.cancelMobileCloneHydration();
        }
        window.removeEventListener('resize', this.onMobileResizeBound);
        window.removeEventListener('pageshow', this.onPageShowBound);
        document.removeEventListener('visibilitychange', this.onVisibilityChangeBound);
        if (this.onMediaQueryChangeBound) {
          if (this.mql.removeEventListener) this.mql.removeEventListener('change', this.onMediaQueryChangeBound);
          else this.mql.removeListener(this.onMediaQueryChangeBound);
        }
        if (this.mobileResizeFrame) cancelAnimationFrame(this.mobileResizeFrame);
        this.cancelMobileAnimation();
        this.cancelMobilePlaybackRetry();
      }

      onSlideChanged(event) {
        if (!this.mql.matches) return;
        this.updateMobileDots(event.detail.currentPage - 1);
        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      onMobilePointerDown(event) {
        if (
          this.mql.matches ||
          event.isPrimary === false ||
          event.button > 0 ||
          (event.pointerType === 'touch' && !event.matchyTouchFallback)
        )
          return;
        this.ensureMobileLoopClones();
        this.mobileHasInteracted = true;
        this.hydrateMobileLoopClones();
        this.cancelMobileAnimation(true);
        this.preloadMobileNeighbors(this.getClosestMobileMediaIndex());
        if (event.matchyTouchFallback) this.bindMobileTouchEvents();
        else this.bindMobilePointerEvents();
        this.mobilePointerState = {
          pointerId: event.pointerId,
          inputType: event.matchyTouchFallback ? 'touch' : 'pointer',
          x: event.clientX,
          y: event.clientY,
          lastX: event.clientX,
          lastTime: event.timeStamp,
          velocity: this.mobileVelocity || 0,
          axis: null,
          index: this.getClosestMobileMediaIndex(),
          trackIndex: this.getClosestMobileTrackIndex(),
          translateX: this.mobilePosition ?? -(this.mobileIndex + 1) * this.mobileSlider.clientWidth,
          width: this.mobileSlider.clientWidth,
          pageXOffset: window.scrollX,
          pageYOffset: window.scrollY,
        };
      }

      onMobilePointerMove(event) {
        const state = this.mobilePointerState;
        if (this.mql.matches || !state || state.pointerId !== event.pointerId) return;
        const coalescedEvents = event.getCoalescedEvents?.();
        const point = coalescedEvents?.length ? coalescedEvents[coalescedEvents.length - 1] : event;
        const deltaX = point.clientX - state.x;
        const deltaY = point.clientY - state.y;

        if (!state.axis) {
          const distanceX = Math.abs(deltaX);
          const distanceY = Math.abs(deltaY);
          if (state.inputType === 'touch') {
            if (distanceY >= 7 && distanceY > distanceX * 1.05) {
              this.stopMobileDragLoop(false);
              this.mobileSlider.classList.remove('is-dragging');
              this.finishMobilePointerTracking();
              this.mobilePointerState = null;
              return;
            }
            if (distanceX <= 5 || distanceX < distanceY * 1.2) return;
          } else if (distanceX <= 3) {
            return;
          }
          state.axis = 'x';
          this.pauseGalleryVideos();
          this.blockMobilePageScroll();
          this.mobileSlider.classList.add('is-dragging');
          this.startMobileDragLoop();
        }

        event.preventDefault();
        const elapsed = Math.max(1, point.timeStamp - state.lastTime);
        const instantVelocity = ((point.clientX - state.lastX) / elapsed) * (1000 / 60);
        state.velocity = state.velocity * 0.55 + instantVelocity * 0.45;
        state.lastX = point.clientX;
        state.lastTime = point.timeStamp;

        const minTranslate = -(this.mobileMediaItems.length + 1) * state.width;
        let translateX = state.translateX + deltaX;
        if (translateX > 0) translateX *= 0.22;
        if (translateX < minTranslate) translateX = minTranslate + (translateX - minTranslate) * 0.22;
        this.mobilePosition = translateX;
        this.mobileVelocity = state.velocity;
        this.mobileDragTarget = translateX;
      }

      onMobilePointerUp(event) {
        const state = this.mobilePointerState;
        if (this.mql.matches || !state || state.pointerId !== event.pointerId) return;
        this.stopMobileDragLoop(true);
        this.finishMobilePointerTracking();
        this.mobilePointerState = null;
        this.mobileSlider.classList.remove('is-dragging');

        if (state.axis !== 'x') {
          this.syncMobileVisibleMedia(true);
          return;
        }

        const deltaX = event.clientX - state.x;
        const width = Math.max(1, state.width);
        const projectedPosition = (this.mobilePosition ?? state.translateX + deltaX) + state.velocity * 8;
        const shouldAdvance = Math.abs(deltaX) > 24 || Math.abs(state.velocity) > 2.5;
        let targetTrackIndex = Math.round(-projectedPosition / width);
        if (shouldAdvance && targetTrackIndex === state.trackIndex) {
          targetTrackIndex += deltaX < 0 || (deltaX === 0 && state.velocity < 0) ? 1 : -1;
        }
        targetTrackIndex = Math.max(
          0,
          Math.min(
            this.mobileMediaItems.length + 1,
            Math.max(state.trackIndex - 2, Math.min(state.trackIndex + 2, targetTrackIndex))
          )
        );
        const targetIndex = this.getMobileIndexForTrackIndex(targetTrackIndex);
        this.mobileSuppressClickUntil = performance.now() + 450;
        this.settleMobileIndex(targetIndex, false, state.velocity, targetTrackIndex);
      }

      onMobilePointerCancel(event) {
        const state = this.mobilePointerState;
        if (!state || state.pointerId !== event.pointerId) return;
        this.stopMobileDragLoop(true);
        this.finishMobilePointerTracking();
        this.mobilePointerState = null;
        this.mobileSlider.classList.remove('is-dragging');
        if (state.axis === 'x') this.settleMobileIndex(this.getClosestMobileMediaIndex(), false, state.velocity);
      }

      onMobileTouchStart(event) {
        if (this.mql.matches || event.touches.length !== 1 || this.mobilePointerState) return;
        const touch = event.touches[0];
        this.onMobilePointerDown({
          pointerId: `touch-${touch.identifier}`,
          pointerType: 'touch',
          matchyTouchFallback: true,
          isPrimary: true,
          button: 0,
          clientX: touch.clientX,
          clientY: touch.clientY,
          timeStamp: event.timeStamp,
        });
      }

      onMobileTouchMove(event) {
        const state = this.mobilePointerState;
        if (!state || state.inputType !== 'touch') return;
        const identifier = Number(state.pointerId.replace('touch-', ''));
        const touch = Array.from(event.touches).find((item) => item.identifier === identifier);
        if (!touch) return;
        this.onMobilePointerMove({
          pointerId: state.pointerId,
          clientX: touch.clientX,
          clientY: touch.clientY,
          timeStamp: event.timeStamp,
          preventDefault: () => event.preventDefault(),
        });
      }

      onMobileTouchEnd(event) {
        const state = this.mobilePointerState;
        if (!state || state.inputType !== 'touch') return;
        const identifier = Number(state.pointerId.replace('touch-', ''));
        const touch = Array.from(event.changedTouches).find((item) => item.identifier === identifier);
        this.onMobilePointerUp({
          pointerId: state.pointerId,
          clientX: touch?.clientX ?? state.lastX,
          clientY: touch?.clientY ?? state.y,
          timeStamp: event.timeStamp,
        });
      }

      onMobileTouchCancel() {
        const state = this.mobilePointerState;
        if (!state || state.inputType !== 'touch') return;
        this.onMobilePointerCancel({ pointerId: state.pointerId });
      }

      bindMobilePointerEvents() {
        if (this.mobilePointerEventsBound) return;
        this.mobilePointerEventsBound = true;
        window.addEventListener('pointermove', this.onMobilePointerMoveBound, { passive: false });
        window.addEventListener('pointerup', this.onMobilePointerUpBound);
        window.addEventListener('pointercancel', this.onMobilePointerCancelBound);
        window.addEventListener('scroll', this.onMobileWindowScrollBound, { passive: true });
      }

      unbindMobilePointerEvents() {
        if (!this.mobilePointerEventsBound) return;
        this.mobilePointerEventsBound = false;
        window.removeEventListener('pointermove', this.onMobilePointerMoveBound);
        window.removeEventListener('pointerup', this.onMobilePointerUpBound);
        window.removeEventListener('pointercancel', this.onMobilePointerCancelBound);
        window.removeEventListener('scroll', this.onMobileWindowScrollBound);
        this.unblockMobilePageScroll();
      }

      bindMobileTouchEvents() {
        if (this.mobileTouchEventsBound) return;
        this.mobileTouchEventsBound = true;
        window.addEventListener('touchmove', this.onMobileTouchMoveBound, { passive: false });
        window.addEventListener('touchend', this.onMobileTouchEndBound);
        window.addEventListener('touchcancel', this.onMobileTouchCancelBound);
        window.addEventListener('scroll', this.onMobileWindowScrollBound, { passive: true });
      }

      unbindMobileTouchEvents() {
        if (!this.mobileTouchEventsBound) return;
        this.mobileTouchEventsBound = false;
        window.removeEventListener('touchmove', this.onMobileTouchMoveBound);
        window.removeEventListener('touchend', this.onMobileTouchEndBound);
        window.removeEventListener('touchcancel', this.onMobileTouchCancelBound);
        window.removeEventListener('scroll', this.onMobileWindowScrollBound);
        this.unblockMobilePageScroll();
      }

      finishMobilePointerTracking() {
        if (this.mobilePointerState?.inputType === 'touch') this.unbindMobileTouchEvents();
        else this.unbindMobilePointerEvents();
      }

      onMobileWindowScroll() {
        const state = this.mobilePointerState;
        if (!state || state.axis === 'x') return;
        const scrolledX = Math.abs(window.scrollX - state.pageXOffset);
        const scrolledY = Math.abs(window.scrollY - state.pageYOffset);
        if (scrolledX <= 3 && scrolledY <= 3) return;
        this.stopMobileDragLoop(false);
        this.mobileSlider.classList.remove('is-dragging');
        this.finishMobilePointerTracking();
        this.mobilePointerState = null;
      }

      blockMobilePageScroll() {
        if (this.mobileTouchMoveBlocker) return;
        this.mobileTouchMoveBlocker = (event) => event.preventDefault();
        document.addEventListener('touchmove', this.mobileTouchMoveBlocker, { passive: false });
      }

      unblockMobilePageScroll() {
        if (!this.mobileTouchMoveBlocker) return;
        document.removeEventListener('touchmove', this.mobileTouchMoveBlocker);
        this.mobileTouchMoveBlocker = null;
      }

      startMobileDragLoop() {
        if (this.mobileDragFrame) return;
        const render = () => {
          this.mobileDragFrame = null;
          if (!this.mobilePointerState || this.mobilePointerState.axis !== 'x' || this.mql.matches) return;
          if (Number.isFinite(this.mobileDragTarget)) this.setTrackTranslateX(this.mobileDragTarget);
          this.mobileDragFrame = requestAnimationFrame(render);
        };
        this.mobileDragFrame = requestAnimationFrame(render);
      }

      stopMobileDragLoop(flushPosition = false) {
        if (this.mobileDragFrame) cancelAnimationFrame(this.mobileDragFrame);
        this.mobileDragFrame = null;
        if (flushPosition && Number.isFinite(this.mobileDragTarget)) this.setTrackTranslateX(this.mobileDragTarget);
        this.mobileDragTarget = null;
      }

      onMobileClick(event) {
        if (this.mql.matches || performance.now() >= (this.mobileSuppressClickUntil || 0)) return;
        this.mobileSuppressClickUntil = 0;
        event.preventDefault();
        event.stopImmediatePropagation();
      }

      onMobileNavigationClick(event) {
        if (this.mql.matches) return;
        const button = event.target.closest('button[name="previous"], button[name="next"]');
        if (!button || button.disabled) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        const direction = button.name === 'next' ? 1 : -1;
        this.settleMobileIndex(this.wrapMobileIndex(this.mobileIndex + direction));
      }

      onMobileResize() {
        if (this.mql.matches || this.mobileResizeFrame) return;
        this.mobileResizeFrame = requestAnimationFrame(() => {
          this.mobileResizeFrame = null;
          this.settleMobileIndex(this.mobileIndex, true);
        });
      }

      settleMobileIndex(index, immediate = false, initialVelocity = 0, requestedTrackIndex = null) {
        if (this.mql.matches || !this.mobileMediaItems.length) return;
        this.ensureMobileLoopClones();
        this.cancelMobileAnimation(true);
        const targetIndex = this.wrapMobileIndex(index);
        const target = this.mobileMediaItems[targetIndex];
        const width = this.mobileSlider.clientWidth;
        const currentTranslate = this.mobilePosition ?? -(this.mobileIndex + 1) * width;
        const targetTrackIndex =
          requestedTrackIndex ?? this.getClosestTrackTargetForIndex(targetIndex, currentTranslate);
        const targetTranslate = -targetTrackIndex * width;
        const finalTranslate = -(targetIndex + 1) * width;
        this.mobileIndex = targetIndex;
        this.mobilePointerState = null;
        this.mobileSlider.classList.remove('is-dragging');
        this.preloadMobileNeighbors(targetIndex);
        this.syncMobileSelection(target, 'auto');

        const finish = () => {
          if (this.mobileSettleTimer) clearTimeout(this.mobileSettleTimer);
          this.mobileSettleTimer = null;
          if (this.mobileTransitionEndBound) {
            this.mobileSlider.removeEventListener('transitionend', this.mobileTransitionEndBound);
            this.mobileTransitionEndBound = null;
          }
          this.mobileSlider.style.transition = 'none';
          this.mobilePosition = finalTranslate;
          this.mobileVelocity = 0;
          this.setTrackTranslateX(finalTranslate);
          this.mobileSlider.classList.remove('is-settling');
          this.mobileIsSettling = false;
          this.activateMobileMedia(target, true, 'auto');
        };

        if (
          immediate ||
          Math.abs(currentTranslate - targetTranslate) < 0.5 ||
          window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ) {
          finish();
          return;
        }

        this.pauseGalleryVideos();
        this.mobilePosition = currentTranslate;
        this.mobileVelocity = 0;
        this.mobileIsSettling = true;
        this.mobileSlider.classList.add('is-settling');

        const slideDistance = Math.abs(targetTranslate - currentTranslate) / Math.max(1, width);
        const velocityReduction = Math.min(70, Math.abs(initialVelocity) * 5);
        const duration = Math.round(Math.max(220, Math.min(340, 295 + slideDistance * 35 - velocityReduction)));
        this.mobileSlider.style.transition = `transform ${duration}ms cubic-bezier(0.22, 1, 0.36, 1)`;
        this.mobileTransitionEndBound = (event) => {
          if (event.target === this.mobileSlider && event.propertyName === 'transform') finish();
        };
        this.mobileSlider.addEventListener('transitionend', this.mobileTransitionEndBound);
        this.mobileSettleTimer = window.setTimeout(finish, duration + 90);
        requestAnimationFrame(() => {
          if (!this.mobileIsSettling || this.mql.matches) return;
          this.mobilePosition = targetTranslate;
          this.setTrackTranslateX(targetTranslate);
        });
      }

      cancelMobileAnimation(preservePosition = false) {
        const renderedPosition = preservePosition ? this.getRenderedTrackTranslateX() : null;
        this.stopMobileDragLoop(false);
        if (this.mobilePointerEventsBound) this.unbindMobilePointerEvents();
        if (this.mobileTouchEventsBound) this.unbindMobileTouchEvents();
        if (this.mobileSettleTimer) clearTimeout(this.mobileSettleTimer);
        this.mobileSettleTimer = null;
        if (this.mobileTransitionEndBound && this.mobileSlider) {
          this.mobileSlider.removeEventListener('transitionend', this.mobileTransitionEndBound);
        }
        this.mobileTransitionEndBound = null;
        if (this.mobileSlider) this.mobileSlider.style.transition = 'none';
        if (Number.isFinite(renderedPosition)) {
          this.mobilePosition = renderedPosition;
          this.setTrackTranslateX(renderedPosition);
        }
        this.mobileSlider?.classList.remove('is-settling');
        this.mobileIsSettling = false;
      }

      setTrackTranslateX(value) {
        this.mobileSlider.style.transform = `translate3d(${value}px, 0, 0)`;
      }

      getRenderedTrackTranslateX() {
        if (!this.mobileSlider) return null;
        const transform = window.getComputedStyle(this.mobileSlider).transform;
        if (!transform || transform === 'none') return this.mobilePosition ?? 0;
        if (window.DOMMatrixReadOnly) return new DOMMatrixReadOnly(transform).m41;
        const values = transform.match(/matrix(?:3d)?\(([^)]+)\)/)?.[1]?.split(',').map(Number);
        if (!values) return this.mobilePosition ?? 0;
        return values.length === 16 ? values[12] : values[4];
      }

      positionMobileTrack() {
        if (this.mql.matches || !this.mobileSlider) return;
        this.ensureMobileLoopClones();
        this.mobileSlider.scrollLeft = 0;
        this.mobileSlider.style.transition = 'none';
        this.mobilePosition = -(this.mobileIndex + 1) * this.mobileSlider.clientWidth;
        this.mobileVelocity = 0;
        this.setTrackTranslateX(this.mobilePosition);
      }

      restoreMobileGallery(forcePlayback) {
        if (this.mql.matches || !this.mobileMediaItems.length) return;
        this.mobileIndex = this.clampMobileIndex(this.mobileIndex);
        this.positionMobileTrack();
        this.activateMobileMedia(this.mobileMediaItems[this.mobileIndex], forcePlayback);
      }

      resetDesktopGallery() {
        this.cancelMobileAnimation();
        this.removeMobileLoopClones();
        this.mobilePosition = null;
        this.mobileVelocity = 0;
        this.mobileSlider.classList.remove('is-dragging', 'is-settling');
        this.mobileSlider.style.removeProperty('transform');
        this.mobileSlider.style.removeProperty('transition');
      }

      clampMobileIndex(index) {
        return Math.max(0, Math.min(this.mobileMediaItems.length - 1, index));
      }

      wrapMobileIndex(index) {
        const count = this.mobileMediaItems.length;
        if (!count) return 0;
        return ((index % count) + count) % count;
      }

      getMobileIndexForTrackIndex(trackIndex) {
        const count = this.mobileMediaItems.length;
        if (!count) return 0;
        if (trackIndex <= 0) return count - 1;
        if (trackIndex >= count + 1) return 0;
        return trackIndex - 1;
      }

      getClosestMobileTrackIndex() {
        if (!this.mobileMediaItems.length) return 1;
        const width = Math.max(1, this.mobileSlider.clientWidth);
        const translateX = this.mobilePosition ?? -(this.mobileIndex + 1) * width;
        return Math.max(0, Math.min(this.mobileMediaItems.length + 1, Math.round(-translateX / width)));
      }

      getClosestTrackTargetForIndex(index, currentTranslate) {
        const count = this.mobileMediaItems.length;
        const width = Math.max(1, this.mobileSlider.clientWidth);
        const currentTrackPosition = -currentTranslate / width;
        const candidates = [index + 1];
        if (index === 0) candidates.push(count + 1);
        if (index === count - 1) candidates.push(0);
        return candidates.reduce((closest, candidate) =>
          Math.abs(candidate - currentTrackPosition) < Math.abs(closest - currentTrackPosition) ? candidate : closest
        );
      }

      getClosestMobileMediaIndex() {
        if (!this.mobileMediaItems.length) return 0;
        return this.getMobileIndexForTrackIndex(this.getClosestMobileTrackIndex());
      }

      ensureMobileLoopClones() {
        if (this.mql.matches || this.mobileMediaItems.length < 2 || this.mobileLoopPrefix) return;
        this.mobileLoopPrefix = this.createMobileLoopClone(
          this.mobileMediaItems[this.mobileMediaItems.length - 1],
          'prefix'
        );
        this.mobileLoopSuffix = this.createMobileLoopClone(this.mobileMediaItems[0], 'suffix');
        this.mobileSlider.prepend(this.mobileLoopPrefix);
        this.mobileSlider.append(this.mobileLoopSuffix);
        this.scheduleMobileCloneHydration();
      }

      createMobileLoopClone(source, position) {
        const clone = source.cloneNode(true);
        clone.removeAttribute('id');
        clone.classList.remove('is-active');
        clone.classList.add('matchy-gallery-loop-clone', `matchy-gallery-loop-clone--${position}`);
        clone.removeAttribute('data-media-id');
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('role', 'presentation');
        clone.setAttribute('inert', '');
        clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
        clone.querySelectorAll('[data-media-id]').forEach((element) => element.removeAttribute('data-media-id'));
        clone.querySelectorAll('[aria-controls], [aria-describedby]').forEach((element) => {
          element.removeAttribute('aria-controls');
          element.removeAttribute('aria-describedby');
        });
        clone.querySelectorAll('img').forEach((image) => {
          const src = image.getAttribute('src');
          const srcset = image.getAttribute('srcset');
          const sizes = image.getAttribute('sizes');
          if (src) image.dataset.matchyCloneSrc = src;
          if (srcset) image.dataset.matchyCloneSrcset = srcset;
          if (sizes) image.dataset.matchyCloneSizes = sizes;
          image.removeAttribute('src');
          image.removeAttribute('srcset');
          image.removeAttribute('sizes');
          image.loading = 'lazy';
          image.fetchPriority = 'low';
        });
        clone.querySelectorAll('video').forEach((video) => {
          video.autoplay = false;
          video.removeAttribute('autoplay');
          video.muted = true;
          video.preload = 'metadata';
          video.pause();
        });
        return clone;
      }

      scheduleMobileCloneHydration() {
        if (this.mobileHasInteracted || this.mobileCloneHydrationTimer || this.mobileCloneIdleHandle) return;
        const activeImage = this.mobileMediaItems[this.mobileIndex]?.querySelector('img');
        const scheduleWhenCalm = () => {
          if (!this.isConnected || this.mobileHasInteracted || this.mobileCloneHydrationTimer) return;
          this.mobileCloneHydrationTimer = window.setTimeout(() => {
            this.mobileCloneHydrationTimer = null;
            const hydrate = () => {
              this.mobileCloneIdleHandle = null;
              if (!this.isConnected) return;
              this.hydrateMobileLoopClones();
              this.preloadMobileNeighbors(this.mobileIndex, true);
            };

            if ('requestIdleCallback' in window) {
              this.mobileCloneIdleHandle = window.requestIdleCallback(hydrate, { timeout: 2500 });
            } else {
              this.mobileCloneIdleHandle = window.setTimeout(hydrate, 500);
            }
          }, 700);
        };

        if (!activeImage || activeImage.complete) scheduleWhenCalm();
        else activeImage.addEventListener('load', scheduleWhenCalm, { once: true });
      }

      cancelMobileCloneHydration() {
        if (this.mobileCloneHydrationTimer) window.clearTimeout(this.mobileCloneHydrationTimer);
        if (this.mobileCloneIdleHandle) {
          if ('cancelIdleCallback' in window) window.cancelIdleCallback(this.mobileCloneIdleHandle);
          else window.clearTimeout(this.mobileCloneIdleHandle);
        }
        this.mobileCloneHydrationTimer = null;
        this.mobileCloneIdleHandle = null;
      }

      hydrateMobileLoopClones() {
        this.cancelMobileCloneHydration();
        [this.mobileLoopPrefix, this.mobileLoopSuffix].forEach((clone) => {
          clone?.querySelectorAll('img[data-matchy-clone-src], img[data-matchy-clone-srcset]').forEach((image) => {
            if (image.dataset.matchyCloneSizes) image.setAttribute('sizes', image.dataset.matchyCloneSizes);
            if (image.dataset.matchyCloneSrcset) image.setAttribute('srcset', image.dataset.matchyCloneSrcset);
            if (image.dataset.matchyCloneSrc) image.setAttribute('src', image.dataset.matchyCloneSrc);
            delete image.dataset.matchyCloneSrc;
            delete image.dataset.matchyCloneSrcset;
            delete image.dataset.matchyCloneSizes;
            image.loading = 'eager';
            image.fetchPriority = 'low';
            if (typeof image.decode === 'function') image.decode().catch(() => {});
          });
        });
      }

      removeMobileLoopClones() {
        this.mobileLoopPrefix?.remove();
        this.mobileLoopSuffix?.remove();
        this.mobileLoopPrefix = null;
        this.mobileLoopSuffix = null;
      }

      preloadMobileNeighbors(index, includeNext = this.mobileHasInteracted) {
        const seen = new Set();
        const mediaIndexes = includeNext ? [index, index + 1] : [index];
        mediaIndexes.forEach((mediaIndex) => {
          mediaIndex = this.wrapMobileIndex(mediaIndex);
          if (seen.has(mediaIndex)) return;
          seen.add(mediaIndex);
          const mediaItem = this.mobileMediaItems[mediaIndex];
          if (!mediaItem) return;
          const image = mediaItem.querySelector('img');
          if (!image) return;
          image.loading = 'eager';
          if (typeof image.decode === 'function') image.decode().catch(() => {});
        });
      }

      syncMobileVisibleMedia(forcePlayback = false) {
        if (this.mql.matches || !this.mobileSlider || this.mobileIsSettling || !this.mobileMediaItems.length) return;
        const activeMedia = this.mobileMediaItems[this.mobileIndex];
        if (!activeMedia || (!forcePlayback && activeMedia.dataset.mediaId === this.mobileActiveMediaId)) return;
        this.activateMobileMedia(activeMedia, forcePlayback);
      }

      activateMobileMedia(activeMedia, forcePlayback = false, thumbnailBehavior = 'auto') {
        if (!activeMedia || (!forcePlayback && activeMedia.dataset.mediaId === this.mobileActiveMediaId)) return;
        this.syncMobileSelection(activeMedia, thumbnailBehavior);
        this.cancelMobilePlaybackRetry();
        this.pauseInactiveVideos(activeMedia);
        this.playActiveMedia(activeMedia);
      }

      syncMobileSelection(activeMedia, thumbnailBehavior = 'auto') {
        if (!activeMedia) return;
        this.mobileActiveMediaId = activeMedia.dataset.mediaId;
        this.mobileMediaItems.forEach((mediaItem) => mediaItem.classList.toggle('is-active', mediaItem === activeMedia));
        const activeIndex = this.mobileMediaItems.indexOf(activeMedia);
        this.mobileIndex = activeIndex;
        this.preloadMobileNeighbors(activeIndex);
        this.updateMobileDots(activeIndex);
        const thumbnail = this.elements.thumbnails?.querySelector(`[data-target="${activeMedia.dataset.mediaId}"]`);
        if (thumbnail) this.setActiveThumbnail(thumbnail, thumbnailBehavior);
      }

      pauseGalleryVideos() {
        this.elements.viewer.querySelectorAll('video').forEach((video) => {
          if (!video.paused) video.pause();
        });
      }

      pauseInactiveVideos(activeItem) {
        this.elements.viewer.querySelectorAll('video').forEach((video) => {
          if (!activeItem.contains(video) && !video.paused) video.pause();
        });
      }

      syncVideoPresentation() {
        const mobile = !this.mql.matches;
        this.querySelectorAll('.matchy-product-video video').forEach((video) => {
          this.prepareInlineVideo(video);
          const videoPoster = video
            .closest('.product-media-container.media-type-video')
            ?.querySelector(':scope > .product__modal-opener--video');
          video.controls = !mobile;
          if (mobile) {
            if (this.isAppleTouchDevice) {
              videoPoster?.setAttribute('inert', '');
              videoPoster?.setAttribute('aria-hidden', 'true');
              this.prepareAppleVideoFrameHandoff(video);
            }
            video.removeAttribute('controls');
            video.setAttribute('controlslist', 'nodownload nofullscreen noremoteplayback');
            video.setAttribute('disablepictureinpicture', '');
            video.setAttribute('disableremoteplayback', '');
            video.setAttribute('x-webkit-airplay', 'deny');
            if ('disablePictureInPicture' in video) video.disablePictureInPicture = true;
            if ('disableRemotePlayback' in video) video.disableRemotePlayback = true;
          }
          if (!mobile || !this.isAppleTouchDevice) {
            videoPoster?.removeAttribute('inert');
            videoPoster?.removeAttribute('aria-hidden');
          }
          if (!mobile) {
            video.setAttribute('controls', '');
            video.removeAttribute('controlslist');
            video.removeAttribute('disablepictureinpicture');
            video.removeAttribute('disableremoteplayback');
            video.removeAttribute('x-webkit-airplay');
            if ('disablePictureInPicture' in video) video.disablePictureInPicture = false;
            if ('disableRemotePlayback' in video) video.disableRemotePlayback = false;
          }
        });
      }

      prepareInlineVideo(video) {
        video.muted = true;
        video.defaultMuted = true;
        video.volume = 0;
        video.playsInline = true;
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        if (video.ended) video.currentTime = 0;
      }

      prepareAppleVideoFrameHandoff(video) {
        const container = video.closest('.product-media-container.media-type-video');
        if (!container || container.classList.contains('is-video-frame-pending')) return;
        const posterImage = container.querySelector(':scope > .product__modal-opener--video img');
        if (!posterImage) return;
        container.classList.add('is-video-frame-pending');
        posterImage.loading = 'eager';
        if (!posterImage.complete && typeof posterImage.decode === 'function') {
          posterImage.decode().catch(() => {});
        }

        const revealVideoFrame = () => {
          if (container.classList.contains('is-video-frame-ready')) return;
          requestAnimationFrame(() => container.classList.add('is-video-frame-ready'));
        };

        if (video.currentTime > 0 && video.readyState >= 2) {
          requestAnimationFrame(revealVideoFrame);
        } else if (typeof video.requestVideoFrameCallback === 'function') {
          video.requestVideoFrameCallback(revealVideoFrame);
        } else {
          video.addEventListener('playing', () => requestAnimationFrame(revealVideoFrame), { once: true });
        }
      }

      startMobileVideo(video, activeItem) {
        this.prepareInlineVideo(video);
        video.autoplay = true;
        video.setAttribute('autoplay', '');
        const playToken = Symbol(activeItem.dataset.mediaId);
        this.mobilePlayToken = playToken;
        let canPlayRetryAdded = false;

        const tryPlay = () => {
          if (
            this.mobilePlayToken !== playToken ||
            this.mobileActiveMediaId !== activeItem.dataset.mediaId ||
            document.visibilityState === 'hidden'
          ) {
            return;
          }
          const playPromise = video.play();
          if (!playPromise) return;
          playPromise
            .then(() => {
              if (this.mobilePlayRetry) clearTimeout(this.mobilePlayRetry);
              this.mobilePlayRetry = null;
            })
            .catch(() => {
              if (!canPlayRetryAdded && video.readyState < 3) {
                canPlayRetryAdded = true;
                video.addEventListener('canplay', tryPlay, { once: true });
              }
              if (!this.mobilePlayRetry) {
                this.mobilePlayRetry = setTimeout(() => {
                  this.mobilePlayRetry = null;
                  tryPlay();
                }, 650);
              }
            });
        };
        tryPlay();
      }

      cancelMobilePlaybackRetry() {
        this.mobilePlayToken = null;
        if (this.mobilePlayRetry) clearTimeout(this.mobilePlayRetry);
        this.mobilePlayRetry = null;
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.mobileMediaItems.find((mediaItem) => mediaItem.dataset.mediaId === mediaId) ||
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.mobileMediaItems[0];
        if (!activeMedia) return;

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
          this.mobileMediaItems = Array.from(this.mobileSlider.querySelectorAll(':scope > [data-media-id]'));
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches) this.settleMobileIndex(this.mobileMediaItems.indexOf(activeMedia));
          else if (this.elements.thumbnails) activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top <= -0.5) {
            window.scrollTo({ top: activeMediaRect.top + window.scrollY, behavior: 'smooth' });
          }
        });

        if (this.mql.matches) {
          this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
            element.classList.toggle('is-active', element === activeMedia);
          });
          this.playActiveMedia(activeMedia);
        }

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail) return;
        this.setActiveThumbnail(activeThumbnail, 'auto');
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      initMobileDots() {
        if (!this.elements.mobileDots) return;
        this.elements.mobileDots.querySelectorAll('[data-gallery-dot]').forEach((dot) => {
          dot.addEventListener('click', (event) => {
            const index = Number(event.currentTarget.dataset.galleryDot);
            const slide = this.mobileMediaItems[index];
            if (!slide) return;
            if (!this.mql.matches) this.settleMobileIndex(index);
            else if (this.elements.viewer.setSlidePosition) this.elements.viewer.setSlidePosition(slide.offsetLeft);
            this.updateMobileDots(index);
          });
        });
      }

      updateMobileDots(activeIndex) {
        if (!this.elements.mobileDots || activeIndex < 0) return;
        this.elements.mobileDots.querySelectorAll('[data-gallery-dot]').forEach((dot, index) => {
          const isActive = index === activeIndex;
          dot.classList.toggle('is-active', isActive);
          if (isActive) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      }

      setActiveThumbnail(thumbnail, behavior = 'auto') {
        if (!this.elements.thumbnails || !thumbnail) return;
        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (!this.mql.matches) {
          const slider = this.elements.thumbnails.slider;
          const maxScroll = Math.max(0, slider.scrollWidth - slider.clientWidth);
          const centered = thumbnail.offsetLeft + thumbnail.offsetWidth / 2 - slider.clientWidth / 2;
          const target = Math.max(0, Math.min(maxScroll, centered));
          if (Math.abs(slider.scrollLeft - target) > 1) slider.scrollTo({ left: target, behavior });
          return;
        }
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft, behavior });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => this.elements.liveRegion.setAttribute('aria-hidden', true), 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        const directVideo = activeItem.querySelector('.matchy-product-video video');
        if (directVideo) {
          if (!this.mql.matches) this.startMobileVideo(directVideo, activeItem);
          else {
            this.prepareInlineVideo(directVideo);
            const playPromise = directVideo.play();
            if (playPromise) playPromise.catch(() => {});
          }
          return;
        }

        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (!deferredMedia) return;
        if (!deferredMedia.hasAttribute('loaded')) deferredMedia.loadContent(false);
        const video = deferredMedia.querySelector('video');
        if (!video) return;
        this.prepareInlineVideo(video);
        const playPromise = video.play();
        if (playPromise) playPromise.catch(() => {});
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
